import { Typography } from '@mui/material'
import CONSTANTS, { PAGES_ROUTES } from '../../../Constants'
import { ASSOCIATED_ITEMS } from '../../../Constants/Texts'
import { getDateInFormat } from '../../../Helpers/Util'
import AssetStateLabel from '../../Labels/AssetStateLabel'
import style from './AssociatedItems.module.scss'

export interface ListingData {
  redirectViewAPiRouteMidPoint: string
  entityType: string
  displayName: string
}

export const isAssetOpsModule = (mod?: string) => {
  if (!mod) return false
  const lowerMod = mod.toLowerCase()
  return (
    mod === CONSTANTS.ASSOCIATED_MODULE_ASSET_OPS ||
    lowerMod === 'asset-operations' ||
    lowerMod === 'assetops' ||
    lowerMod === 'asset_ops'
  )
}

export const isIrisSegmentModule = (mod?: string) =>
  mod === CONSTANTS.ASSOCIATED_MODULE_IRIS_SEGMENT

export const getSearchPlaceholder = (mod?: string) => {
  if (mod === 'parameter-repository') {
    return ASSOCIATED_ITEMS.LISTING.SEARCH_PLACE_HOLDER_FOR_PARAMETER
  }
  return ASSOCIATED_ITEMS.LISTING.SEARCH_PLACE_HOLDER
}

// Renders the segment status as a styled Typography with active / inactive classes.
export const renderSegmentStatusBadge = (status?: string) => {
  const isActive = (status || '').toUpperCase() === 'ACTIVE'
  return (
    <Typography className={`${style.status} ${isActive ? style?.active : style?.inactive}`}>
      {isActive ? 'Active' : 'In Active'}
    </Typography>
  )
}

// Maps a raw segment record from /segment/list into the row shape consumed by
// the Segments tab of the Associated Items screen.
export const mapSegmentRecord = (ele: any) => ({
  _id: ele?._id,
  segmentTitle: ele?.segmentTitle,
  segmentId: ele?.segmentId,
  description: ele?.description,
  country: ele?.country || 'NA',
  cluster: ele?.cluster || 'NA',
  segmentStatus: ele?.segmentStatus ? renderSegmentStatusBadge(ele.segmentStatus) : 'NA',
  packName: ele?.packName || 'NA',
  packFamily: ele?.packFamily || 'NA',
  platform: ele?.platform || 'NA',
  minimumVersion: ele?.minimumVersion || 'NA',
  maximumVersion: ele?.maximumVersion || 'NA',
  selectVersion: ele?.selectedVersion || 'NA',
  creationDate: getDateInFormat(ele?.creationDate as string, CONSTANTS.DATE_FORMAT_TIME_SECONDS),
  lastUpdatedDate: getDateInFormat(
    ele?.lastUpdatedDate as string,
    CONSTANTS.DATE_FORMAT_TIME_SECONDS,
  ),
  rowURL: `/${PAGES_ROUTES.ManageSegment}/${PAGES_ROUTES.PageTypeView}/${ele?._id}`,
})

export const getTabConfig = (tabKey: string, isParamRepo: boolean) => {
  const tabConfigs: Record<string, any> = {
    container: {
      columns: isParamRepo
        ? CONSTANTS.ASSOCIATED_PARAMETER_REPOSITORY_LISTING_COLUMNS_FOR_CONTAINER
        : CONSTANTS.ASSOCIATED_CONTAINER_LISTING_COLUMNS,
      listDetail: {
        redirectViewAPiRouteMidPoint: isParamRepo
          ? `${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.ContainerDefinitionList}`
          : PAGES_ROUTES.ManageContainer,
        entityType: 'container',
        displayName: 'container',
      },
    },
    widget: {
      columns: isParamRepo
        ? CONSTANTS.ASSOCIATED_PARAMETER_REPOSITORY_LISTING_COLUMNS
        : CONSTANTS.ASSOCIATED_WIDGET_LISTING_COLUMNS,
      listDetail: {
        redirectViewAPiRouteMidPoint: isParamRepo
          ? `${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.WidgetDefinitionList}`
          : PAGES_ROUTES.ManageWidget,
        entityType: 'widget',
        displayName: 'widget',
      },
    },
    siteNavigation: {
      columns: isParamRepo
        ? CONSTANTS.ASSOCIATED_PARAMETER_REPOSITORY_LISTING_COLUMNS
        : CONSTANTS.ASSOCIATED_SITE_NAVIGATION_LISTING_COLUMNS,
      listDetail: {
        redirectViewAPiRouteMidPoint: isParamRepo
          ? `${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.WidgetDefinitionList}`
          : PAGES_ROUTES.SiteNavigation,
        entityType: 'siteNavigation',
        displayName: 'site navigation',
      },
    },
    segment: {
      columns: CONSTANTS.ASSOCIATED_IRIS_SEGMENT_LISTING_COLUMNS,
      listDetail: {
        redirectViewAPiRouteMidPoint: PAGES_ROUTES.ManageSegment,
        entityType: 'segment',
        displayName: 'segment',
      },
    },
  }
  return tabConfigs[tabKey]
}

export const getFilterOptions = (
  id: 'type' | 'layout' | 'state',
  lable: string,
  options: any,
  searchable = true,
) => {
  return {
    id,
    lable,
    searchable,
    clearall: true,
    options,
  }
}

export const getDocumentTitle = (detailData: any, module?: string): string => {
  if (detailData?.segmentName) {
    return `Associated Items - ${detailData.segmentName}`
  }
  if (detailData?.parameterName) {
    return `Associated Items - ${detailData.parameterName}`
  }
  if (detailData?.jobName || detailData?.jobId) {
    return `Associated Items - ${detailData.jobName || detailData.jobId}`
  }
  if (detailData?.title && detailData.title !== 'NA') {
    return `Associated Items - ${detailData.title}`
  }
  if (isIrisSegmentModule(module) && (detailData?.value || detailData?.key)) {
    return `Associated Items - ${detailData.value || detailData.key}`
  }
  return 'Associated Items'
}

export const getAssociationModulePayload = (module?: string) => {
  let moduleDataName = `${module?.toLowerCase()}Id`
  let modulePayloadName = module
  if (module === CONSTANTS.ASSOCIATED_MODULE_SITENAVIGATION) {
    modulePayloadName = 'siteNavigation'
    moduleDataName = 'siteNavigationId'
  } else if (isAssetOpsModule(module)) {
    modulePayloadName = 'asset_ops_job'
    moduleDataName = 'jobId'
  } else if (isIrisSegmentModule(module)) {
    modulePayloadName = 'iris_segment'
    moduleDataName = 'key'
  }
  return { moduleDataName, modulePayloadName }
}

export const renderStatusBadge = (status?: string) => (
  <Typography
    className={`${style.status} ${status === 'ACTIVE' ? style?.active : style?.inactive}`}
  >
    {status === 'ACTIVE' ? 'Active' : 'In Active'}
  </Typography>
)

export const formatEngine = (engine?: string) => {
  if (!engine || engine === 'NA') return 'NA'
  return engine.charAt(0).toUpperCase() + engine.slice(1).toLowerCase()
}

// Fields shared by the widget / container / siteNavigation listing records.
const buildCommonListingRecord = (ele: any, redirectMidpoint: string) => ({
  _id: ele?._id,
  title: ele?.title,
  type: ele?.type,
  layout: ele?.layout,
  segment: ele?.segment,
  createdAt: getDateInFormat(ele?.createdAt ?? '', CONSTANTS.DATE_FORMAT_TIME_SECONDS),
  updatedAt: getDateInFormat(ele?.updatedAt ?? '', CONSTANTS.DATE_FORMAT_TIME_SECONDS),
  lastFeaturedDate: getDateInFormat(
    ele?.lastFeaturedDate ?? '',
    CONSTANTS.DATE_FORMAT_TIME_SECONDS,
  ),
  state: <AssetStateLabel state={ele?.stateDisplayName} />,
  status: renderStatusBadge(ele?.status),
  versionCount: ele?.versionCount,
  rowURL: `/${redirectMidpoint}/${PAGES_ROUTES.PageTypeView}/${ele?._id}`,
})

export const transformListingRecords = (
  tabKey: string,
  redirectMidpoint: string,
  records: any[] = [],
): any[] => {
  switch (tabKey) {
    case 'widget':
      return records.map((ele) => ({
        ...buildCommonListingRecord(ele, redirectMidpoint),
        widgetId: ele?.widgetId,
        widgetTypeTitle: ele?.widgetTypeTitle,
        engine: formatEngine(ele?.engine),
      }))

    case 'container':
      return records.map((ele) => ({
        ...buildCommonListingRecord(ele, redirectMidpoint),
        containerId: ele?.containerId,
        containerTypeTitle: ele?.containerTypeTitle,
        language: ele?.language,
        isChildPageRequired: ele?.isChildPageRequired,
        isVersionRequired: ele?.isVersionRequired,
        isCloneRequired: ele?.isCloneRequired,
        contentListingPage: ele?.contentListingPage,
      }))

    case 'siteNavigation':
      return records.map((ele) => ({
        ...buildCommonListingRecord(ele, redirectMidpoint),
        siteNavigationId: ele?.siteNavigationId,
        widgetTypeTitle: ele?.widgetTypeTitle,
      }))

    case 'segment':
      return records.map((ele) => mapSegmentRecord(ele))

    default:
      return []
  }
}
